import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';

function SettingsCISReverseVAT() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  // CIS Settings
  const [cisEnabled, setCisEnabled] = useState(false);
  const [cisCompanyUTR, setCisCompanyUTR] = useState('');
  const [cisStandardRate, setCisStandardRate] = useState('20');
  const [cisHigherRate, setCisHigherRate] = useState('30');
  const [cisGrossRate, setCisGrossRate] = useState('0');

  // Reverse VAT Settings
  const [reverseVatEnabled, setReverseVatEnabled] = useState(false);
  const [reverseVatNote, setReverseVatNote] = useState('');
  const [reverseVatServices, setReverseVatServices] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await apiFetch('/api/company-settings');
      const data = await response.json();

      if (data.cis_enabled) setCisEnabled(data.cis_enabled);
      if (data.cis_company_utr) setCisCompanyUTR(data.cis_company_utr);
      if (data.cis_standard_rate) setCisStandardRate(data.cis_standard_rate);
      if (data.cis_higher_rate) setCisHigherRate(data.cis_higher_rate);
      if (data.cis_gross_rate) setCisGrossRate(data.cis_gross_rate);

      if (data.reverse_vat_enabled) setReverseVatEnabled(data.reverse_vat_enabled);
      if (data.reverse_vat_note) setReverseVatNote(data.reverse_vat_note);
      if (data.reverse_vat_services) setReverseVatServices(data.reverse_vat_services);
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiFetch('/api/company-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cis_enabled: cisEnabled,
          cis_company_utr: cisCompanyUTR,
          cis_standard_rate: parseFloat(cisStandardRate),
          cis_higher_rate: parseFloat(cisHigherRate),
          cis_gross_rate: parseFloat(cisGrossRate),
          reverse_vat_enabled: reverseVatEnabled,
          reverse_vat_note: reverseVatNote,
          reverse_vat_services: reverseVatServices
        })
      });

      if (response.ok) {
        alert('Settings saved successfully!');
      } else {
        alert('Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Error saving settings');
    }
    setSaving(false);
  };

  return (
    <div className="p-6 max-w-4xl">
      <button
        onClick={() => navigate('/settings')}
        className="text-blue-600 hover:text-blue-800 mb-4"
      >
        ← Back to Settings
      </button>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">CIS & Reverse VAT</h1>
      <p className="text-gray-600 mb-6">Configure Construction Industry Scheme and Reverse VAT settings</p>

      {/* CIS Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Construction Industry Scheme (CIS)</h2>
            <p className="text-sm text-gray-600 mt-1">Manage CIS deductions for subcontractors</p>
          </div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={cisEnabled}
              onChange={(e) => setCisEnabled(e.target.checked)}
              className="mr-2 h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-sm font-medium">Enable CIS</span>
          </label>
        </div>

        {cisEnabled && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ CIS Overview:</strong> The Construction Industry Scheme requires contractors to deduct money from subcontractors' payments and pass it to HMRC. The deduction counts as advance payment towards the subcontractor's tax and National Insurance.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Company UTR (Unique Taxpayer Reference)
                </label>
                <input
                  type="text"
                  value={cisCompanyUTR}
                  onChange={(e) => setCisCompanyUTR(e.target.value)}
                  placeholder="e.g., 1234567890"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Required for CIS monthly returns and verification
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Standard Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={cisStandardRate}
                    onChange={(e) => setCisStandardRate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default: 20%</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Higher Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={cisHigherRate}
                    onChange={(e) => setCisHigherRate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default: 30%</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gross Payment (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={cisGrossRate}
                    onChange={(e) => setCisGrossRate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default: 0%</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">CIS Rate Guide:</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li><strong>20% (Standard):</strong> Verified subcontractors with valid CIS registration</li>
                  <li><strong>30% (Higher):</strong> Unverified/unregistered subcontractors</li>
                  <li><strong>0% (Gross):</strong> Gross payment status holders (no deductions)</li>
                </ul>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Reverse VAT Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Domestic Reverse Charge (Reverse VAT)</h2>
            <p className="text-sm text-gray-600 mt-1">VAT reverse charge for building and construction services</p>
          </div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={reverseVatEnabled}
              onChange={(e) => setReverseVatEnabled(e.target.checked)}
              className="mr-2 h-5 w-5 text-blue-600 rounded"
            />
            <span className="text-sm font-medium">Enable Reverse VAT</span>
          </label>
        </div>

        {reverseVatEnabled && (
          <>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-900">
                <strong>⚠️ Important:</strong> The domestic reverse charge applies to certain building and construction services from 1 March 2021. The customer (not the supplier) accounts for the VAT.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Note Text
                </label>
                <textarea
                  value={reverseVatNote}
                  onChange={(e) => setReverseVatNote(e.target.value)}
                  rows={3}
                  placeholder="e.g., Customer to account for reverse charge VAT..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This text will appear on invoices subject to reverse charge
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Applicable Services (Optional)
                </label>
                <textarea
                  value={reverseVatServices}
                  onChange={(e) => setReverseVatServices(e.target.value)}
                  rows={4}
                  placeholder="List services subject to reverse charge (e.g., Construction, Installation, Demolition...)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Internal reference - helps identify which services trigger reverse charge
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Reverse Charge Applies To:</h3>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>Construction, alteration, repair, extension, demolition</li>
                  <li>Installation of heat, light, air-con, ventilation, power, water</li>
                  <li>Internal cleaning of buildings post-construction</li>
                  <li>Painting or decorating of building exteriors</li>
                </ul>
                <p className="text-xs text-gray-500 mt-2">
                  ⚠️ Only applies when customer is VAT-registered and end-user is not in the chain
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

export default SettingsCISReverseVAT;
